import { PrintIcon } from "assets/icons";
import { GoBackHeaderTitle } from "components";
import { useManageTable } from "hooks";
import { BoxShadowLayout, ContentLayout } from "layouts";
import moment from "moment";
import {
  COMPANY_DELIVERIES_LABELS,
  DeliveriesTable
} from "modules/financial";
import { useState } from "react";
import { Value } from "react-date-picker/dist/cjs/shared/types";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  DateRangeInput,
  Pagination,
  PersonSummary,
  SearchInput
} from "ui";

import { useCompanyDriverDeliveries } from "../../apis";
import { useCurrentPortalUser } from "../../hooks";

import styles from "./CompanyDriverDeliveries.module.scss";

const PAGE_SIZE = 10;

const CompanyDriverDeliveries = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { driverId } = useParams();
  const { company } = useCurrentPortalUser();

  const {
    searchValue,
    setSearchValue,
    debouncedSearch,
    page,
    setPage,
    sorting,
    setSorting
  } = useManageTable({ defaultSorting: [{ id: "DeliveryDate", desc: true }] });

  const [dateRange, setDateRange] = useState<Value>(null);

  const {
    driver,
    deliveries,
    meta,
    allPagesDeliveries,
    isLoading,
    refetch
  } = useCompanyDriverDeliveries(company?.id ?? "", {
    driverId: String(driverId),
    search: debouncedSearch,
    from:
      Array.isArray(dateRange) && dateRange[0]
        ? moment(dateRange[0]).format("YYYY-MM-DD")
        : moment().subtract(10, "year").format("YYYY-MM-DD"),
    to:
      Array.isArray(dateRange) && dateRange[1]
        ? moment(dateRange[1]).format("YYYY-MM-DD")
        : moment().add(1, "year").format("YYYY-MM-DD"),
    page,
    take: PAGE_SIZE,
    orderBy: sorting[0]?.id,
    order: sorting[0] ? (sorting[0]?.desc ? "DESC" : "ASC") : "DESC"
  });

  return (
    <ContentLayout
      title={
        <GoBackHeaderTitle
          title={t("Deliveries")}
          onGoBack={() => navigate({ pathname: "/company/financial" })}
        />
      }
    >
      <BoxShadowLayout className={styles.driverDeliveries}>
        <PersonSummary
          isLoading={isLoading}
          personId={driver?.id}
          personFullName={
            driver ? `${driver.firstname} ${driver.lastname}` : ""
          }
          personImage={driver?.image}
          statistic={[
            {
              label: t("Earned from customers"),
              value: driver?.totalAmountEarnedFromCustomers
            },
            {
              label: t(COMPANY_DELIVERIES_LABELS.toCollect),
              value: driver?.totalAmountToCollect
            },
            {
              label: t(COMPANY_DELIVERIES_LABELS.toPay),
              value: driver?.totalAmountToPay
            },
            {
              label: t(COMPANY_DELIVERIES_LABELS.paid),
              value: driver?.totalAmountPaid
            },
            {
              label: t(COMPANY_DELIVERIES_LABELS.collected),
              value: driver?.totalAmountCollected
            }
          ]}
        />

        <div className={styles.driverDeliveries_tableHeader}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={t("Search")}
          />

          <div className={styles.driverDeliveries_tableHeader_rightBlock}>
            <DateRangeInput value={dateRange} onChange={setDateRange} />

            <Button
              className={styles.driverDeliveries_tableHeader_printBtn}
              icon={<PrintIcon />}
              onClick={() => allPagesDeliveries.mutate()}
              loading={allPagesDeliveries.isLoading}
            >
              {t("Print Report")}
            </Button>
          </div>
        </div>

        <DeliveriesTable
          items={deliveries ?? []}
          sorting={sorting}
          setSorting={setSorting}
          isLoading={isLoading}
          refetchDeliveries={refetch}
          labels={COMPANY_DELIVERIES_LABELS}
          hideActions
        />

        <Pagination
          currentPage={page}
          onChange={setPage}
          totalItems={meta?.itemCount}
        />
      </BoxShadowLayout>
    </ContentLayout>
  );
};

export default CompanyDriverDeliveries;
